/* eslint-disable no-unused-vars */
import { HTMLAttributes, ReactNode, RefObject, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

import { useLenis } from '@/contexts/lenis'
import { MediaQuery } from '@/constants'

import { getTimelineParallaxOptions } from '../sections/animations'
import { cn } from '@/lib/utils'

export type ImageParallaxProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
> & {
  children: (ref: RefObject<HTMLImageElement | HTMLVideoElement>) => ReactNode
  nodeRef?: RefObject<HTMLElement>
  revertOnUpdate?: boolean
  disable?: boolean
}

const ImageParallax = ({
  nodeRef,
  children,
  disable,
  revertOnUpdate = true,
  ...props
}: ImageParallaxProps) => {
  const root = useRef<HTMLDivElement>(null!)
  const image = useRef<HTMLImageElement | HTMLVideoElement>(null)
  const { lenis } = useLenis()

  useGSAP(
    () => {
      if (disable || image.current instanceof HTMLVideoElement) return
      if (!lenis) return

      const matchMedia = gsap.matchMedia(root.current)

      matchMedia.add(
        {
          isSmall: MediaQuery.SMALL,
          isLarge: MediaQuery.LARGE
        },
        ({ conditions }) => {
          if (conditions?.isSmall) return

          const parallax = gsap
            .timeline({ ...getTimelineParallaxOptions(root.current) })
            .fromTo(image.current, { xPercent: -15 }, { xPercent: 0 })

          return () => {
            parallax.kill()
          }
        }
      )

      return () => {
        matchMedia.revert()
      }
    },
    {
      dependencies: [lenis, disable],
      scope: nodeRef || root,
      revertOnUpdate
    }
  )

  return disable ? (
    children(image)
  ) : (
    <span
      ref={root}
      className={cn(
        'relative isolate block h-full w-full overflow-hidden',
        'lg:[&>*]:!w-[115%] lg:[&>*]:max-w-none'
      )}
      {...props}
    >
      {children(image)}
    </span>
  )
}

export default ImageParallax
